/* PD size guide — "Find your size" on the product page (GEAR_BUILDER.md §6–8).

   What it does: on a sized product (helmet, gloves, boots, jacket, pants …) that has an APPROVED
   manufacturer chart, a "Find your size" button appears under the variant picker. The customer
   types their body measurements once; the size engine (pd-size-engine.js, the same code the
   importer and the admin tester run) compares them with the manufacturer's own chart and
   recommends a size, or says honestly that it cannot (outside the chart, a garment-measure chart).
   One tap selects that size in the variant picker.

   Rules this file keeps:
   - No approved chart → nothing is shown, not even the button. No guessing (§6).
   - Measurements never leave the device: the engine runs here, the profile lives in localStorage
     (per-device convenience; every read/write is try/catch so a blocked storage still works).
   - The engine and the stylesheet load only on products that have a chart, so every other
     product page pays one small cached request and nothing else.
   - Every visible string comes from the section's pd.size.* locale keys (no text in this file).
   - px, not rem, in the CSS: Hyper sets html { font-size: 10px } (see pd-size-guide.css).
   - init() is idempotent per root and runs again on pd:navigated. */
(function () {
  "use strict";

  var PROFILE_KEY = "pd_size_profile_v1";
  // Display order of the measurement fields: top of the body to the feet.
  var DIM_ORDER = ["head", "neck", "chest", "underbust", "waist", "hip", "sleeve", "arm", "torso", "thigh", "inseam", "knee", "calf", "height",
    "handCirc", "handLength", "palmWidth", "footLength", "shoeEU"];
  var ICON_RULER = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19.875 12c.621 0 1.125 .512 1.125 1.143v5.714c0 .631 -.504 1.143 -1.125 1.143h-15.875a1 1 0 0 1 -1 -1v-5.857c0 -.631 .504 -1.143 1.125 -1.143h15.75z"/><path d="M9 12v2"/><path d="M6 12v3"/><path d="M12 12v3"/><path d="M18 12v3"/><path d="M15 12v2"/><path d="M3 3v4"/><path d="M3 5h18"/><path d="M21 3v4"/></svg>';
  var ICON_X = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>';

  // ---------------------------------------------------------------------------------------------
  // small helpers
  // ---------------------------------------------------------------------------------------------
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function fill(s, o) {
    return String(s || "").replace(/\{(\w+)\}/g, function (m, k) { return o[k] != null ? o[k] : m; });
  }
  function readJSON(root, sel) {
    try { return JSON.parse(root.querySelector(sel).textContent); } catch (e) { return null; }
  }
  function loadProfile() {
    try {
      var p = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
      if (p && typeof p === "object") return { m: p.m || {}, answers: p.answers || {} };
    } catch (e) { /* storage blocked or corrupt: start empty */ }
    return { m: {}, answers: {} };
  }
  function saveProfile(p) {
    try { localStorage.setItem(PROFILE_KEY, JSON.stringify({ m: p.m, answers: p.answers })); } catch (e) { /* private mode */ }
  }
  function num(v) {
    var n = Number(String(v == null ? "" : v).trim().replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  function fmtNum(x) { return String(Math.round(x * 10) / 10).replace(".", ","); }
  function fmtRange(v) {
    if (!v) return "–";
    return v[0] === v[1] ? fmtNum(v[0]) : fmtNum(v[0]) + "–" + fmtNum(v[1]);
  }

  var pending = {};
  function loadScript(src) {
    if (window.PdSizeEngine) return Promise.resolve();
    if (pending[src]) return pending[src];
    pending[src] = new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = src; s.async = true;
      s.onload = function () { window.PdSizeEngine ? resolve() : reject(new Error("engine missing")); };
      s.onerror = function () { delete pending[src]; reject(new Error("engine failed to load")); };
      document.head.appendChild(s);
    });
    return pending[src];
  }
  function loadCss(href) {
    if (!href || document.querySelector('link[data-pd-size-css]')) return Promise.resolve();
    return new Promise(function (resolve) {
      var l = document.createElement("link");
      l.rel = "stylesheet"; l.href = href; l.setAttribute("data-pd-size-css", "");
      l.onload = l.onerror = function () { resolve(); };
      document.head.appendChild(l);
      setTimeout(resolve, 3000); // never hold the button back on a slow stylesheet
    });
  }

  // ---------------------------------------------------------------------------------------------
  // the product's variants and the theme's variant picker
  // ---------------------------------------------------------------------------------------------
  function visible(el) { return !!(el && (el.offsetParent || el.getClientRects().length)); }
  function pickerEl() {
    var all = document.querySelectorAll("variant-selects");
    for (var i = 0; i < all.length; i++) if (visible(all[i])) return all[i];
    return all[0] || null;
  }
  function currentVariantId(st) {
    var vs = pickerEl();
    var cur = vs && readJSON(vs, "[data-selected-variant]");
    if (cur && cur.id) return String(cur.id);
    var q = new URLSearchParams(location.search).get("variant");
    return q || (st.variants[0] && String(st.variants[0].id));
  }

  /** Which Shopify option holds the size: the one whose values follow the size keys. */
  function sizeOptionIndex(st) {
    var n = st.variants[0] ? st.variants[0].o.length : 0;
    for (var i = 0; i < n; i++) {
      var ok = true, seen = {}, distinct = 0;
      st.data.variants.forEach(function (v) {
        var lv = st.byId[v.variantId];
        if (!lv || !v.sizeKey) return;
        var val = lv.o[i];
        if (seen[val] == null) { seen[val] = v.sizeKey; distinct++; } else if (seen[val] !== v.sizeKey) ok = false;
      });
      if (ok && distinct > 1) return i;
    }
    // One size on sale (or one option only): the option whose values are sizes at all.
    return n === 1 ? 0 : -1;
  }

  /** The variant that sells size `key`: same colour as the one on screen if possible, in stock first. */
  function variantFor(st, key) {
    var E = window.PdSizeEngine;
    var ids = st.data.variants.filter(function (v) {
      return v.sizeKey && (E.variantCovers(v.sizeKey, key) || E.variantCovers(key, v.sizeKey));
    }).map(function (v) { return v.variantId; });
    var cands = ids.map(function (id) { return st.byId[id]; }).filter(Boolean);
    if (!cands.length) return null;
    var cur = st.byId[currentVariantId(st)];
    var si = st.sizeIdx;
    function score(lv) {
      var same = 0;
      if (cur) for (var i = 0; i < lv.o.length; i++) if (i !== si && lv.o[i] === cur.o[i]) same++;
      return same * 2 + (lv.a ? 1 : 0);
    }
    cands.sort(function (a, b) { return score(b) - score(a); });
    return cands[0];
  }

  /** Put the picker on variant `lv`. One option to change → click it (the theme does the rest);
      several → a real navigation to ?variant=, which every theme understands. */
  function selectVariant(st, lv) {
    var vs = pickerEl();
    var cur = st.byId[currentVariantId(st)];
    var diff = [];
    if (cur) for (var i = 0; i < lv.o.length; i++) if (lv.o[i] !== cur.o[i]) diff.push(i);
    if (cur && !diff.length) return true;
    if (vs && diff.length === 1) {
      var groups = vs.querySelectorAll(".product-form__input");
      var g = groups[diff[0]];
      if (g) {
        var value = lv.o[diff[0]];
        var sel = g.querySelector("select");
        if (sel) {
          sel.value = value;
          sel.dispatchEvent(new Event("change", { bubbles: true }));
          return true;
        }
        var inputs = g.querySelectorAll('input[type="radio"]');
        for (var j = 0; j < inputs.length; j++) {
          if (inputs[j].value === value) { inputs[j].click(); return true; }
        }
      }
    }
    var u = new URL(location.href);
    u.searchParams.set("variant", lv.id);
    location.assign(u.toString());
    return false;
  }

  // ---------------------------------------------------------------------------------------------
  // the recommendation
  // ---------------------------------------------------------------------------------------------
  function bodyCharts(st) {
    return st.data.charts.filter(function (c) { return c.mode !== "garment"; });
  }

  /** Measurements to ask for: required (primary + must-fit) first, then optional ones that help. */
  function fieldsFor(st) {
    var req = {}, opt = {};
    bodyCharts(st).forEach(function (c) {
      c.dims.forEach(function (d) {
        if (d.role === "info" && c.measure && c.measure[d.key] && c.measure[d.key].comparable === false) return; // never used
        if (d.role === "primary" || d.role === "fit") req[d.key] = true; else opt[d.key] = true;
      });
    });
    // Charts that complement each other (one per leg length) are told apart by a dim that is only
    // "info" inside each chart — for the customer it is required, or no length can be chosen.
    var charts = bodyCharts(st);
    if (charts.length > 1) {
      Object.keys(opt).forEach(function (k) {
        if (!charts.every(function (c) { return c.dims.some(function (d) { return d.key === k; }); })) return;
        var vals = charts.map(function (c) { var r = c.rows[0]; return JSON.stringify(r && r.v && r.v[k] || null); });
        if (vals.some(function (v) { return v !== vals[0]; })) req[k] = true;
      });
    }
    Object.keys(req).forEach(function (k) { delete opt[k]; });
    var order = function (a, b) { return DIM_ORDER.indexOf(a) - DIM_ORDER.indexOf(b); };
    return { required: Object.keys(req).sort(order), optional: Object.keys(opt).sort(order) };
  }

  function asksFor(st) {
    var out = [], seen = {};
    bodyCharts(st).forEach(function (c) {
      (c.advice || []).forEach(function (a) { if (!seen[a.ask]) { seen[a.ask] = true; out.push(a); } });
    });
    return out;
  }

  function compute(st) {
    var charts = bodyCharts(st);
    if (!charts.length) return { status: "no_advice", reasons: [], warnings: [], needs: [], questions: [] };
    var ctx = {
      winter: st.winter,
      sellable: st.data.variants.map(function (v) { return v.sizeKey; }).filter(Boolean),
    };
    return window.PdSizeEngine.recommendAcross(charts, { m: st.profile.m, answers: st.profile.answers }, ctx);
  }

  /** How a size is written to the customer: the label the shop sells it under, else the chart's. */
  function sizeLabel(st, key) {
    var lv = variantFor(st, key);
    if (lv && st.sizeIdx >= 0) return lv.o[st.sizeIdx];
    var E = window.PdSizeEngine;
    for (var i = 0; i < st.data.charts.length; i++) {
      var c = st.data.charts[i];
      for (var j = 0; j < c.rows.length; j++) if (E.rowKey(c, c.rows[j]) === key) return c.rows[j].label;
    }
    return key;
  }

  function dimName(st, d) { return st.S["dim_" + d] || d; }

  function resultHTML(st, r) {
    var S = st.S;
    var brand = st.brandName;
    if (r.status === "no_advice") return '<p class="pd-sg__msg">' + esc(S.garment) + "</p>";
    if (r.status === "need") {
      var filled = Object.keys(st.profile.m).some(function (k) { return num(st.profile.m[k]) != null; });
      if (!filled) return '<p class="pd-sg__msg pd-sg__msg--muted">' + esc(S.need) + "</p>";
      return '<p class="pd-sg__msg pd-sg__msg--muted">' + esc(fill(S.need_more, { dims: r.needs.map(function (d) { return dimName(st, d); }).join(", ") })) + "</p>";
    }
    if (r.status === "outside") {
      var above = r.reasons.some(function (x) { return x.code === "above_chart" || x.code === "fit_dim_above_chart"; });
      return '<div class="pd-sg__verdict pd-sg__verdict--none"><p class="pd-sg__msg">' +
        esc(fill(above ? S.outside_above : S.outside_below, { brand: brand })) + "</p></div>";
    }
    if (!r.size) return '<p class="pd-sg__msg pd-sg__msg--muted">' + esc(S.need) + "</p>";
    // The size is settled but not the variant (leg length): ask for the one measurement, show no size yet.
    if (r.needs && r.needs.length) {
      return '<p class="pd-sg__msg pd-sg__msg--muted">' + r.needs.map(function (d) { return esc(fill(S.add_dim, { dim: dimName(st, d) })); }).join("<br>") + "</p>";
    }

    var label = sizeLabel(st, r.size);
    var lines = [];
    var disagree = r.reasons.some(function (x) { return x.code === "charts_disagree"; });
    if (disagree && r.alternative) lines.push(fill(S.disagree, { size: label, alt: sizeLabel(st, r.alternative) }));
    else if (r.alternative) {
      lines.push(fill(S.between, { size: label, alt: sizeLabel(st, r.alternative) }));
      var tiebreak = r.reasons.filter(function (x) { return x.code === "tiebreak"; })[0];
      if (tiebreak) lines.push(fill(S.tiebreak, { dim: dimName(st, tiebreak.dim) }));
      else lines.push(st.tie === "down" ? S.tie_down : S.tie_up);
    } else lines.push(fill(S.fit, { brand: brand }));
    r.reasons.forEach(function (x) {
      if (x.code === "edge_of_chart") lines.push(S.edge);
      else if (x.code === "bumped_by") lines.push(fill(S.bumped, { dim: dimName(st, x.dim) }));
      else if (x.code === "decided_by") lines.push(fill(S.decided_by, { dim: dimName(st, x.dim) }));
      else if (x.code === "from_shoe_size") lines.push(S.from_shoe);
      else if (x.code === "brand_advice") lines.push(fill(S.advice_applied, { brand: brand }));
      else if (x.code === "chosen_by") lines.push(fill(S.chosen_by, { dim: dimName(st, x.dim) }));
      else if (x.code === "add_dim_to_choose") lines.push(fill(S.add_dim, { dim: dimName(st, x.dim) }));
    });
    var warns = (r.warnings || []).map(function (w) {
      var long = /_long_for_size$/.test(w.code);
      return fill(long ? S.warn_long : S.warn_short, { dim: dimName(st, w.dim), cm: fmtNum(w.cm) });
    });

    var lv = variantFor(st, r.size);
    var cta;
    // The size is certain but the variant is not (the leg length needs one more measurement):
    // no "Choose" button until it is given — picking a length for them would be a guess.
    if (r.needs && r.needs.length) cta = "";
    else if (!lv) cta = '<p class="pd-sg__note">' + esc(fill(S.not_sold, { size: label })) + "</p>";
    else if (!lv.a) cta = '<p class="pd-sg__note">' + esc(fill(S.sold_out, { size: label })) + "</p>";
    else if (String(lv.id) === String(currentVariantId(st))) cta = '<p class="pd-sg__note pd-sg__note--ok">' + esc(fill(S.selected, { size: label })) + "</p>";
    else cta = '<button type="button" class="pd-sg__cta" data-sg-select="' + esc(lv.id) + '">' + esc(fill(S.select, { size: label })) + "</button>";

    return '<div class="pd-sg__verdict">' +
      '<p class="pd-sg__eyebrow">' + esc(S.recommended) + "</p>" +
      '<p class="pd-sg__size">' + esc(label) + "</p>" +
      lines.map(function (l) { return '<p class="pd-sg__line">' + esc(l) + "</p>"; }).join("") +
      (warns.length ? '<ul class="pd-sg__warn">' + warns.map(function (w) { return "<li>" + esc(w) + "</li>"; }).join("") + "</ul>" : "") +
      cta + "</div>";
  }

  // ---------------------------------------------------------------------------------------------
  // markup
  // ---------------------------------------------------------------------------------------------
  function fieldHTML(st, d, optional) {
    var S = st.S;
    var v = st.profile.m[d];
    var unit = d === "shoeEU" ? "EU" : "cm";
    return '<label class="pd-sg__field">' +
      '<span class="pd-sg__label">' + esc(dimName(st, d)) + (optional ? ' <em>' + esc(S.optional) + "</em>" : "") + "</span>" +
      '<span class="pd-sg__input"><input type="text" inputmode="decimal" autocomplete="off" name="' + esc(d) + '" value="' +
      esc(v == null ? "" : v) + '" aria-describedby="pd-sg-how-' + esc(d) + '"><span class="pd-sg__unit">' + unit + "</span></span>" +
      '<span class="pd-sg__how" id="pd-sg-how-' + esc(d) + '">' + esc(S["how_" + d] || "") + "</span>" +
      "</label>";
  }

  function tableHTML(st, c, recKey) {
    var S = st.S, E = window.PdSizeEngine;
    var dims = c.dims.map(function (d) { return d.key; });
    var head = "<tr><th scope=\"col\">" + esc(S.size) + "</th>" + dims.map(function (d) {
      return '<th scope="col">' + esc(dimName(st, d)) + (d === "shoeEU" ? "" : ' <span class="pd-sg__u">cm</span>') + "</th>";
    }).join("") + "</tr>";
    var body = c.rows.map(function (row) {
      var k = E.rowKey(c, row);
      var rec = recKey && (k === recKey);
      return "<tr" + (rec ? ' class="is-rec"' : "") + '><th scope="row">' + esc(row.label) + "</th>" +
        dims.map(function (d) { return "<td>" + esc(fmtRange(row.v && row.v[d])) + "</td>"; }).join("") + "</tr>";
    }).join("");
    var shape = c.headShape ? '<p class="pd-sg__meta">' + esc(S.head_shape) + ": " + esc(S["shape_" + c.headShape.replace(/-/g, "_")] || c.headShape) + "</p>" : "";
    var garment = c.mode === "garment" ? '<p class="pd-sg__meta">' + esc(S.garment) + "</p>" : "";
    return '<figure class="pd-sg__chart">' +
      '<figcaption class="pd-sg__caption">' + esc(c.title) + "</figcaption>" + shape + garment +
      '<div class="pd-sg__scroll"><table class="pd-sg__table"><thead>' + head + "</thead><tbody>" + body + "</tbody></table></div>" +
      (c.source && c.source.url ? '<a class="pd-sg__source" href="' + esc(c.source.url) + '" target="_blank" rel="noopener nofollow">' + esc(S.source) + "</a>" : "") +
      "</figure>";
  }

  function buildDialog(st) {
    var S = st.S;
    var f = fieldsFor(st);
    var asks = asksFor(st);
    var canAdvise = bodyCharts(st).length > 0;
    var d = document.createElement("dialog");
    d.className = "pd-sg";
    d.setAttribute("aria-labelledby", "pd-sg-title");
    d.innerHTML =
      '<div class="pd-sg__panel">' +
        '<div class="pd-sg__head">' +
          '<div><h2 class="pd-sg__title" id="pd-sg-title">' + esc(S.title) + "</h2>" +
          '<p class="pd-sg__sub">' + esc(st.brandName) + "</p></div>" +
          '<button type="button" class="pd-sg__x" data-sg-close aria-label="' + esc(S.close) + '">' + ICON_X + "</button>" +
        "</div>" +
        (canAdvise
          ? '<div class="pd-sg__tabs" role="tablist">' +
              '<button type="button" role="tab" id="pd-sg-t1" aria-controls="pd-sg-p1" aria-selected="true" data-sg-tab="measure">' + esc(S.tab_measure) + "</button>" +
              '<button type="button" role="tab" id="pd-sg-t2" aria-controls="pd-sg-p2" aria-selected="false" tabindex="-1" data-sg-tab="chart">' + esc(S.tab_chart) + "</button>" +
            "</div>"
          : "") +
        '<div class="pd-sg__body">' +
          (canAdvise
            ? '<section class="pd-sg__pane" id="pd-sg-p1" role="tabpanel" aria-labelledby="pd-sg-t1" data-sg-pane="measure">' +
                '<p class="pd-sg__intro">' + esc(fill(S.intro, { brand: st.brandName })) + "</p>" +
                '<form class="pd-sg__form" novalidate>' +
                  f.required.map(function (x) { return fieldHTML(st, x, false); }).join("") +
                  f.optional.map(function (x) { return fieldHTML(st, x, true); }).join("") +
                  asks.map(function (a) {
                    return '<label class="pd-sg__check"><input type="checkbox" name="ask:' + esc(a.ask) + '"' +
                      (st.profile.answers[a.ask] === true ? " checked" : "") + "><span>" + esc(S["ask_" + a.ask] || a.text || a.ask) + "</span></label>";
                  }).join("") +
                "</form>" +
                '<div class="pd-sg__result" aria-live="polite" data-sg-result></div>' +
                '<p class="pd-sg__privacy">' + esc(S.privacy) + "</p>" +
              "</section>"
            : "") +
          '<section class="pd-sg__pane" id="pd-sg-p2" role="tabpanel"' + (canAdvise ? ' aria-labelledby="pd-sg-t2" hidden' : "") + ' data-sg-pane="chart"></section>' +
        "</div>" +
      "</div>";
    document.body.appendChild(d);
    return d;
  }

  // ---------------------------------------------------------------------------------------------
  // wiring
  // ---------------------------------------------------------------------------------------------
  function refresh(st) {
    var r = compute(st);
    st.last = r;
    var out = st.dialog && st.dialog.querySelector("[data-sg-result]");
    if (out) out.innerHTML = resultHTML(st, r);
    var pane = st.dialog && st.dialog.querySelector('[data-sg-pane="chart"]');
    if (pane) pane.innerHTML = st.data.charts.map(function (c) { return tableHTML(st, c, r.size); }).join("");
    // The chip beside the button: the answer without opening anything, once measurements exist.
    var chip = st.root.querySelector("[data-sg-chip]");
    if (chip) {
      if (r.size && !(r.needs && r.needs.length)) { chip.textContent = fill(st.S.your_size, { size: sizeLabel(st, r.size) }); chip.hidden = false; }
      else chip.hidden = true;
    }
  }

  function setTab(st, name) {
    st.dialog.querySelectorAll("[data-sg-tab]").forEach(function (b) {
      var on = b.getAttribute("data-sg-tab") === name;
      b.setAttribute("aria-selected", on ? "true" : "false");
      b.tabIndex = on ? 0 : -1;
    });
    st.dialog.querySelectorAll("[data-sg-pane]").forEach(function (p) { p.hidden = p.getAttribute("data-sg-pane") !== name; });
  }

  function open(st) {
    if (!st.dialog) {
      st.dialog = buildDialog(st);
      wireDialog(st);
    }
    refresh(st);
    if (typeof st.dialog.showModal === "function") st.dialog.showModal(); else st.dialog.setAttribute("open", "");
    document.documentElement.classList.add("pd-sg-open");
    var first = st.dialog.querySelector(".pd-sg__form input");
    if (first && !first.value && window.matchMedia("(hover: hover)").matches) first.focus(); // no keyboard pop-up on phones
  }

  function close(st) {
    if (!st.dialog) return;
    if (typeof st.dialog.close === "function" && st.dialog.open) st.dialog.close(); else st.dialog.removeAttribute("open");
  }

  function wireDialog(st) {
    var d = st.dialog;
    var timer = null;
    d.addEventListener("close", function () {
      document.documentElement.classList.remove("pd-sg-open");
      var btn = st.root.querySelector("[data-sg-open]");
      if (btn) btn.focus({ preventScroll: true });
    });
    // A click on the backdrop (the dialog element itself, outside the panel) closes it.
    d.addEventListener("click", function (e) {
      if (e.target === d) { close(st); return; }
      if (e.target.closest("[data-sg-close]")) { close(st); return; }
      var tab = e.target.closest("[data-sg-tab]");
      if (tab) { setTab(st, tab.getAttribute("data-sg-tab")); return; }
      var sel = e.target.closest("[data-sg-select]");
      if (sel) {
        var lv = st.byId[sel.getAttribute("data-sg-select")];
        if (lv && selectVariant(st, lv)) { close(st); setTimeout(function () { refresh(st); }, 400); }
      }
    });
    d.addEventListener("keydown", function (e) {
      var tab = e.target.closest && e.target.closest("[data-sg-tab]");
      if (!tab || (e.key !== "ArrowRight" && e.key !== "ArrowLeft")) return;
      var next = tab.getAttribute("data-sg-tab") === "measure" ? "chart" : "measure";
      setTab(st, next);
      d.querySelector('[data-sg-tab="' + next + '"]').focus();
      e.preventDefault();
    });
    var form = d.querySelector(".pd-sg__form");
    if (form) {
      form.addEventListener("submit", function (e) { e.preventDefault(); });
      form.addEventListener("input", function (e) {
        var t = e.target;
        if (t.type === "checkbox" && t.name.indexOf("ask:") === 0) st.profile.answers[t.name.slice(4)] = t.checked;
        else if (t.name) {
          var v = String(t.value).trim();
          if (v === "") delete st.profile.m[t.name]; else st.profile.m[t.name] = v;
        }
        saveProfile(st.profile);
        clearTimeout(timer);
        timer = setTimeout(function () { refresh(st); }, 180);
      });
    }
  }

  function relocate(root) {
    try {
      var anchor = null;
      var picks = document.querySelectorAll(".product__block--variant_picker");
      for (var i = 0; i < picks.length; i++) if (visible(picks[i])) { anchor = picks[i]; break; }
      if (!anchor) anchor = document.querySelector(".product__block--price");
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(root, anchor.nextSibling);
    } catch (e) { /* stays where the section rendered it */ }
  }

  async function mount(root) {
    var S = readJSON(root, "[data-sg-strings]") || {};
    var variants = readJSON(root, "[data-sg-variants]") || [];
    var base = (root.dataset.apiBase || "https://pd-garage.fly.dev").replace(/\/$/, "");
    var url = new URL("/api/storefront/size-guide", base);
    url.searchParams.set("shop", root.dataset.shop || "");
    url.searchParams.set("product", root.dataset.product || "");
    var res = await fetch(url.toString(), { credentials: "omit" });
    if (!res.ok) return;
    var data = await res.json();
    if (!data || !data.charts || !data.charts.length) return; // no approved chart: stay invisible
    await Promise.all([loadScript(root.dataset.engine), loadCss(root.dataset.css)]);

    var st = {
      root: root, S: S, data: data, variants: variants, byId: {},
      profile: loadProfile(),
      brandName: data.charts[0].brandName || root.dataset.vendor || "",
      winter: /\b(winter|vinter)/i.test(root.dataset.title || ""),
      dialog: null, last: null,
    };
    variants.forEach(function (v) { st.byId[String(v.id)] = v; });
    st.sizeIdx = sizeOptionIndex(st);
    var tie = data.charts[0].tie || (window.PdSizeEngine.SLOT_TIE[data.charts[0].slot]);
    st.tie = typeof tie === "function" ? tie({ winter: st.winter }) : (tie || "up");

    var canAdvise = bodyCharts(st).length > 0;
    root.innerHTML =
      '<div class="pd-sg__bar">' +
        '<button type="button" class="pd-sg__open" data-sg-open aria-haspopup="dialog">' + ICON_RULER +
        "<span>" + esc(canAdvise ? S.open : S.open_chart) + "</span></button>" +
        '<button type="button" class="pd-sg__chip" data-sg-chip aria-haspopup="dialog" hidden></button>' +
      "</div>";
    relocate(root);
    root.hidden = false;
    root.querySelector("[data-sg-open]").addEventListener("click", function () { open(st); });
    root.querySelector("[data-sg-chip]").addEventListener("click", function () { open(st); });
    refresh(st);
  }

  function init() {
    var roots = document.querySelectorAll("[data-pd-size-guide]");
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.dataset.sgReady) continue;
      root.dataset.sgReady = "1";
      if (!root.dataset.product || !root.dataset.shop) continue;
      mount(root).catch(function () { /* the guide is an extra: a failure leaves the page as it was */ });
    }
  }

  function start() {
    // After first paint: the guide is never the first thing a customer needs.
    if ("requestIdleCallback" in window) requestIdleCallback(init, { timeout: 1500 }); else setTimeout(init, 300);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
  document.addEventListener("pd:navigated", start);
})();
